import React, { useState } from "react";
import { Alert, Button } from "react-bootstrap";

import { getBulkProposalTemplate } from "../../../services/documentGeneration.service";

import {
  BulkCustomer,
  describeHttpError,
  downloadBlob,
  filenameFromResponse,
  isCustomerSendable,
  readJsonSafely,
  toCustomerRef,
  xlsxBlobFromResponse,
} from "./bulk.utils";
import BulkCustomerCard from "./bulkCustomerCard";
import BulkCustomerInput from "./bulkCustomerInput";
import BulkWorkbookUpload from "./bulkWorkbookUpload";

// Two independent steps: build and download a blank workbook for a set of
// customers, then (possibly much later) upload the filled-in workbook
const BulkProposalGenerator: React.FC = () => {
  const [customers, setCustomers] = useState<BulkCustomer[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRetrieving, setIsRetrieving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [downloaded, setDownloaded] = useState(false);

  const sendable = customers.filter(isCustomerSendable);
  const skippedCount = customers.length - sendable.length;

  const updateCustomer = (updated: BulkCustomer) => {
    setCustomers((prev) => prev.map((c) => (c.zohoId === updated.zohoId ? updated : c)));
    setDownloaded(false);
  };

  const removeCustomer = (zohoId: string) => {
    setCustomers((prev) => prev.filter((c) => c.zohoId !== zohoId));
    setDownloaded(false);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setErrorMessage("");
    setDownloaded(false);
    try {
      const res = await getBulkProposalTemplate({ customers: sendable.map(toCustomerRef) });
      if (!res.ok) {
        setErrorMessage(describeHttpError(res.status, await readJsonSafely(res)));
        return;
      }
      downloadBlob(
        await xlsxBlobFromResponse(res),
        filenameFromResponse(res, "bulk_proposal_template.xlsx"),
      );
      setDownloaded(true);
    } catch {
      setErrorMessage("An unexpected error occurred. Please check your connection.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="proposal-document-form">
      <section className="mb-5">
        <h2 className="h4">Start a bulk request</h2>
        <p className="text-muted">
          Add customers and choose their meters, then download a workbook to fill in their quotes.
        </p>

        <BulkCustomerInput
          onRetrieved={(found) => {
            // Re-retrieved customers replace their existing card in place
            setCustomers((prev) => [
              ...prev.map((c) => found.find((f) => f.zohoId === c.zohoId) ?? c),
              ...found.filter((f) => !prev.some((c) => c.zohoId === f.zohoId)),
            ]);
            setDownloaded(false);
          }}
          onRetrievingChange={setIsRetrieving}
        />

        {/* Greyed out and locked while a lookup is running */}
        <div
          aria-busy={isRetrieving}
          style={isRetrieving ? { opacity: 0.5, pointerEvents: "none" } : undefined}
        >
          {customers.map((customer) => (
            <BulkCustomerCard
              key={customer.zohoId}
              customer={customer}
              onChange={updateCustomer}
              onRemove={() => {
                removeCustomer(customer.zohoId);
              }}
            />
          ))}
        </div>

        {customers.length > 0 && (
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <Button
              variant="primary"
              disabled={isGenerating || isRetrieving || sendable.length === 0}
              onClick={() => void handleGenerate()}
            >
              {isGenerating
                ? "Generating..."
                : `Download template (${String(sendable.length)} customer${sendable.length === 1 ? "" : "s"})`}
            </Button>
            <Button
              variant="outline-secondary"
              disabled={isGenerating || isRetrieving}
              onClick={() => {
                setCustomers([]);
                setErrorMessage("");
                setDownloaded(false);
              }}
            >
              Clear all
            </Button>
            {skippedCount > 0 && (
              <small className="text-warning">
                {skippedCount} customer{skippedCount === 1 ? " is" : "s are"} missing a name or
                meters and won&apos;t be included
              </small>
            )}
          </div>
        )}

        {errorMessage && (
          <Alert
            variant="danger"
            className="mt-3"
            dismissible
            onClose={() => {
              setErrorMessage("");
            }}
          >
            {errorMessage}
          </Alert>
        )}
        {downloaded && (
          <Alert variant="success" className="mt-3">
            Template downloaded - fill in the quotes and upload it below.
          </Alert>
        )}
      </section>

      <section>
        <h2 className="h4">Submit a completed workbook</h2>
        <BulkWorkbookUpload />
      </section>
    </div>
  );
};

export default BulkProposalGenerator;
